import styled from "styled-components/macro";
import { THEME_COLOR, THEME_COLOR_DARK } from "../vars";
import { Close } from "./Icons";
import { ContactViewerSidePanel } from "./ContactViewer";
import { GroupViewerSidePanel } from "./GroupViewer";
import KeywordSidePanel from "./KeywordManagement/KeywordSidePanel";
import formatPhoneNumber from "../util/formatPhoneNumber";
import { useSkipioQuery } from "../hooks/use-query";

function ContactAvatar({
  contactId,
  groupId,
  keywordId,
  onRemove,
}: {
  contactId?: string;
  groupId?: string;
  keywordId?: string;
  onRemove?: () => void;
}) {
  const groupQuery = useSkipioQuery({
    queryKey: ["groups", groupId],
    enabled: !!groupId,
  });
  const contactQuery = useSkipioQuery({
    queryKey: ["contacts", contactId],
    enabled: !!contactId,
  });
  const keywordQuery = useSkipioQuery({
    queryKey: ["keywords", keywordId],
    enabled: !!keywordId,
  });

  if (groupId) {
    const group = groupQuery.data;
    const initials = group.name[0].toUpperCase();
    return (
      <Container>
        <GroupViewerSidePanel
          groupId={groupId}
          trigger={({ ref, show }) => (
            <Image
              data-ignore="true"
              ref={ref}
              onClick={show}
              disabled={!groupId}
              title={initials}
            />
          )}
        />
        {onRemove && (
          <CloseButton onClick={onRemove}>
            <Close />
          </CloseButton>
        )}
        <Label data-ignore="true">{group.name}</Label>
      </Container>
    );
  } else if (contactId) {
    const contact = contactQuery.data;
    const isNamelessContact = contact.firstName === "Nameless";

    return (
      <Container>
        <ContactViewerSidePanel
          contactId={contactId}
          //@ts-expect-error
          trigger={({ ref, show }) => (
            <Image
              ref={ref}
              data-ignore="true"
              onClick={show}
              disabled={!contactId}
              title={isNamelessContact ? "??" : contact.initials}
            />
          )}
        />
        {onRemove && (
          <CloseButton onClick={onRemove}>
            <Close />
          </CloseButton>
        )}
        <Label data-ignore="true">
          {isNamelessContact
            ? formatPhoneNumber(contact.phoneMobile)
            : contact.fullName}
        </Label>
      </Container>
    );
  } else if (keywordId) {
    const keyword = keywordQuery.data;
    const initials = keyword.keyword[0].toUpperCase();
    return (
      <Container>
        <KeywordSidePanel
          keywordId={keywordId}
          trigger={({ ref, show }) => (
            <Image
              data-ignore="true"
              ref={ref}
              onClick={show}
              disabled={!keywordId}
              title={initials}
            />
          )}
        />
        {onRemove && (
          <CloseButton onClick={onRemove}>
            <Close />
          </CloseButton>
        )}
        <Label data-ignore="true">{keyword.keyword}</Label>
      </Container>
    );
  } else {
    return (
      <Container>
        <Image />
        <Label></Label>
      </Container>
    );
  }
}

const Container = styled.div`
  display: inline-block;
  height: 40px;
  width: 40px;
  position: relative;
  top: -5px;
  margin: 20px;
`;

const Image = styled.button`
  position: absolute;
  display: block;
  background: white;
  border: none;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  border: 1px solid #dcdcdc;
  border-radius: 1000px;
  background-size: cover;
  background-position: 50% 50%;
  &:not([disabled]) {
    &:active {
      transform: scale(0.95);
    }
  }
  &:after {
    display: block;
    content: attr(title);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 12px;
    color: #444;
  }
`;

const CloseButton = styled.button`
  border-radius: 1000px;
  border: 0;
  background: ${THEME_COLOR};
  width: 16px;
  height: 16px;
  transform: translate(25%, -25%);
  position: absolute;
  top: 0;
  right: 0;
  color: white;
  padding: 0;
  font-size: 8px;
  svg {
    width: 100%;
    height: 100%;
    transform: scale(0.7);
  }
  &:active {
    background: ${THEME_COLOR_DARK};
  }
`;

const Label = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translate(-50%, 40%);
  font-size: 10px;
  width: 200%;
  text-align: center;
  margin-bottom: 20px;
`;

export default ContactAvatar;
